/**
 * UpgradeModal Component
 * 
 * Modal for displaying and selecting subscription plans (Base/Premium).
 * Shows pricing for monthly/yearly billing cycles and handles plan selection.
 * Premium plan shows "Coming Soon" state with interest registration.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Callback to close modal
 * @param {Object} props.subscriptionStatus - Current subscription status
 * @param {Function} props.onPlanSelect - Callback when plan is selected
 * @param {Object} props.currentUser - Current logged-in user
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { CheckCircle, Sparkles, RefreshCw, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function UpgradeModal({ isOpen, onClose, subscriptionStatus, onPlanSelect, currentUser }) {
  console.log('🟢 UpgradeModal FUNCTION CALLED, isOpen:', isOpen);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [isInterestSubmitted, setIsInterestSubmitted] = useState(false);
  
  const plans = {
    base: {
      name: 'Base',
      monthly: { price: 19.99, display: '$19.99', planId: 'base_monthly' },
      yearly: { price: 220, display: '$220', save: '$20', planId: 'base_yearly' }
    },
    premium: {
      name: 'Premium',
      monthly: { price: 29.99, display: '$29.99', planId: 'premium_monthly' },
      yearly: { price: 330, display: '$330', save: '$30', planId: 'premium_yearly' }
    }
  };
  
  const handleUpgrade = async (planId) => {
    setLoading(true);
    await onPlanSelect(planId);
    setLoading(false);
  };
  
  const handleNotifyMe = async () => {
    if (isSubmittingInterest || isInterestSubmitted) return;
    
    setIsSubmittingInterest(true);
    try {
      const response = await axios.post(
        `${API}/premium-interest`,
        {
          email: currentUser?.email || '',
          subscribe_to_updates: true
        },
        {
          headers: {
            'X-User-Email': currentUser?.email,
            'X-User-Role': currentUser?.role
          }
        }
      );
      
      setIsInterestSubmitted(true);
      toast.success(response.data.detail || "We'll notify you when Premium launches!");
    } catch (error) {
      console.error('Failed to register interest:', error);
      if (error.response?.data?.detail) {
        if (error.response.data.message === 'already_registered') {
          setIsInterestSubmitted(true);
          toast.info(error.response.data.detail);
        } else {
          toast.error(error.response.data.detail);
        }
      } else {
        toast.error('Failed to register your interest. Please try again.');
      }
    } finally {
      setIsSubmittingInterest(false);
    }
  };
  
  console.log('🔵 UpgradeModal rendering, isOpen:', isOpen);
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto z-[9999]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Choose Your Plan</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Billing Cycle Toggle */}
          <div className="flex justify-center">
            <div className="bg-gray-100 rounded-lg p-1 inline-flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-white shadow-sm text-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-6 py-2 rounded-md font-medium transition-colors ${
                  billingCycle === 'yearly'
                    ? 'bg-white shadow-sm text-orange-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
                <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Save up to $30</span>
              </button>
            </div>
          </div>
          
          {/* Plans Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Base Plan */}
            <Card className="border-2 hover:border-blue-500 transition-colors">
              <CardContent className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-bold mb-2">Base</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold">{plans.base[billingCycle].display}</span>
                    <span className="text-gray-600">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-sm text-green-600 mt-1">Save {plans.base.yearly.save} per year</p>
                  )}
                </div>
                
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">All core features</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Multi-user/team support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Basic reports</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Excel import/export</span>
                  </li>
                </ul>
                
                <button
                  onClick={() => handleUpgrade(plans.base[billingCycle].planId)}
                  disabled={loading}
                  className="btn-bawarchi-primary w-full"
                >
                  {loading ? 'Processing...' : 'Upgrade to Base'}
                </button>
              </CardContent>
            </Card>
            
            {/* Premium Plan - Coming Soon */}
            <Card className="border-2 border-purple-500 relative bg-gradient-to-br from-purple-50 to-white">
              <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                COMING SOON
              </div>
              <CardContent className="p-6">
                <div className="mb-4">
                  <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                    Premium
                    <Sparkles className="w-5 h-5 text-purple-500" />
                  </h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-purple-600">Coming Early 2026</span>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-3">
                    We're building something special! Premium will include:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">Everything in Base</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm"><strong>Advanced Analytics & Reports</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm"><strong>Customer Segmentation</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm"><strong>Customer Ratings & Reviews</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm"><strong>AI-Powered Menu Optimization</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm"><strong>Multi-Store Management</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Sparkles className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm"><strong>Priority Support</strong></span>
                    </li>
                  </ul>
                </div>
                
                {!isInterestSubmitted ? (
                  <button
                    onClick={handleNotifyMe}
                    disabled={isSubmittingInterest}
                    className="w-full bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isSubmittingInterest ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Notify Me When It Launches
                      </>
                    )}
                  </button>
                ) : (
                  <div className="w-full bg-green-100 text-green-800 px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    We'll notify you!
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          <p className="text-sm text-gray-600 text-center">
            Secure payment powered by Stripe • Cancel anytime
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// Floating Contact Us Button Component

export default UpgradeModal;
